import type { SortConfig, PageOptions } from 'src/services/api.types';
import { DocumentFile, DocumentType } from 'src/services/files/file.types';
import type { LinkedOrgCandidate } from 'src/services/candidates/candidates.types';
import type { ICompanyProfile, ContactLinkType } from 'src/services/connections/connections.types';
import { CsvImportRow, EntityLinkType, CsvImportOptions, CsvImportRowResult } from 'src/services/common.types';

import { CustomFieldCellValue } from 'src/components/data-list/custom-field-columns.types';

import { TenantType } from 'src/types/enums';

import { LinkType } from '../common/common.types';
import type { User } from '../organisation/organisation.types';
import type { AttachedThread } from '../threads/threads.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';

export interface ContactProfileLink {
  type: LinkType;
  url: string;
}

export type AddContactProps = {
  org_id: string;
  manual_contact?: {
    title?: string;
    position_title?: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    company_name?: string;
    company_profile_id?: string;
    location?: string;
    type: ContactType;
    custom_fields?: UpdateCustomFieldValueDto;
  }
  account?: string;
  source: ContactSource
};

export type UpdateContactProps = {
  contact_id: string;
  title?: string;
  position_title?: string;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  location?: string;
  special_information?: string;
  links?: ContactProfileLink[];
  linked_candidate_profile_id?: string;
  custom_fields?: UpdateCustomFieldValueDto;
  // Org tree: id of the contact this person reports to. Pass null/'' to clear the manager.
  reports_to_contact_id?: string | null;
  // Not sent to the API — used only to target the CompanyContactLink cache tag so the org
  // chart refetches after a manager change made from within a company view.
  company_profile_id?: string;
}

export type BulkContactImportProps = CsvImportOptions & {
  organization_id?: string;
  /** Row keys follow the backend's ContactDataRow. */
  contacts: CsvImportRow[];
}

export interface ContactImportResult extends CsvImportRowResult {
  email: string;
  /** The company the contact was linked to, and whether the import created it. */
  company?: { name: string; status: 'created' | 'linked' };
}


export interface IContactCompanyLink {
  id: string;
  type: ContactLinkType;
  company_profile: ICompanyProfile;
}

export interface ICreateContactDocumentDto {
  file_id: string;
  name: string;
  type: DocumentType;
}

export interface GetOrgContactsQuery {
  org_id: string;
  page: PageOptions;
  search_query?: string;
  contact_type?: TenantType;
  linked_contacts?: boolean;
  sort_config?: SortConfig;
  /** Per-column search values keyed by column key (item 3). Sent to the lean list as JSON. */
  column_filters?: Record<string, string>;
  /**
   * Restrict to the members of one saved list. Opening a list reuses this endpoint rather than
   * needing one of its own, so it inherits the same search, sort and per-column filters.
   */
  list_id?: string;
}

// Flat row from GET /organizations/:id/contacts/list (the lean list read path, JT1-1527/1528).
export interface IContactListItem {
  id: string;
  first_name: string | null;
  last_name: string | null;
  position_title: string | null;
  contact_type: ContactType | null;
  email: string | null;
  phone: string | null;
  // Contact's own city/region (item 30, JT1-1540/1541).
  location: string | null;
  company_name: string | null;
  account_owner_name: string | null;
  lists: string[];
  // Labels inherited from the contact's linked company (JT1-1541 QA — the "PSA" column became a
  // general "Labels" column). Contacts have no labels of their own.
  labels: string[];
  // LinkedIn quick-link beside the name (JT1-1480).
  linkedin_url: string | null;
  /** Values for this org's custom fields, keyed by config id. */
  custom_fields?: Record<string, CustomFieldCellValue>;
  thread_id: string | null;
  last_activity_at: string | null;
}

export interface RemoveContactProps {
  contact_id: string;
}

export type Contact = {
  id: string;
  title: string;
  position_title?: string;
  first_name: string;
  last_name: string;
  phone: string;
  // JT1-1827: asked not to be called
  do_not_call?: boolean;
  do_not_call_at?: string | null;
  type: TenantType;
  contact_type: ContactType;
  email: string;
  status: string;
  source: ContactSource;
  special_information: string;
  company_name: string;
  location?: string;
  linked_candidate: LinkedOrgCandidate | null;
  thread: AttachedThread;
  account_owner?: Partial<User>;
  links?: ContactProfileLink[];
  // Org tree: id of the contact this person reports to (null when they are a root).
  reports_to_contact_id?: string | null;
  account?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    organization: {
      company_name: string;
      company_information: string;
      company_type: string;
    }
  }
  created_at: '2024-07-16T22:44:56.000Z';
};

export type ContactDocument = {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export enum ContactSource {
  JOB_INTERACTION = 'job_interaction',
  MANUAL_LINK = 'manual_link',
}

export enum ContactType {
  HIRING_MANAGER = 'hiring_manager', // default client type
  DECISION_MAKER = 'decision_maker',
  INTERNAL_TALENT = 'internal_talent',
  EXECUTIVE = 'executive',
  ADVISOR = 'advisor',
  LEGAL = 'legal',
  HR = 'hr',

  OBSERVER = 'observer',

  CONSULTANT = 'consultant', // default recruiter type
  ACCOUNT_MANAGER = 'account_manager',
  TEAM_MANAGER = 'team_manager',
  RESOURCER = 'resourcer',
  ADMINISTRATION = 'administration',
  PROSPECT = 'prospect',

  CANDIDATE = 'candidate', // default candidate type

  OTHER = 'other'
}

export enum ContactLinkEntityType {
  CONTACT = 'contact',
  COMPANY_PROFILE = 'company_profile',
  DEAL = 'deal',
  MEETING = 'meeting'
}

export interface AddContactLinkProps {

  entity_id: string;

  entity_type: ContactLinkEntityType;

  contacts: {
    id: string;
    type: EntityLinkType;
  }[];
}
